/**
 * T-031 — notifications contracts (tasks/W3-CONTRACTS.md §5 · docs/10-design/10-notifications.md §1)
 * NotificationEvent ตรงกับ key ที่ใช้ใน `app_settings.notification_defaults` (packages/schema/src/seed.ts NOTIFICATION_DEFAULTS)
 */
import { z } from "zod";

export const NotificationEvent = z.enum([
  "ticket.new_in_team_inbox",
  "ticket.assigned_to_me",
  "ticket.handoff_received",
  "ticket.returned",
  "message.customer_reply",
  "message.agent_reply",
  "message.note",
  "mention",
  "ticket.status_changed",
  "ticket.due_soon",
  "ticket.overdue",
  "ticket.escalated",
  "ticket.child_closed",
  "ticket.unsnoozed",
  "redaction.revealed",
  // [T-031] ไม่อยู่ใน 10 §1 เดิม แต่จำเป็นสำหรับ automation action {type:"notify"} (W3-CONTRACTS §4/§5) — ไม่มีใน NOTIFICATION_DEFAULTS seed (finding ใน PR)
  "automation.notify",
  "automation.error",
  "csat.received",
  "mailbox.new_mail",
  "outbox.delivery_failed",
  "system.digest",
  // [Lead #66] DSAR ใกล้ครบ/เลยกำหนด 30 วัน (13 §5.2 · W4 §6) — ไม่ผูกกับการ์ด (ticket_id = null)
  "dsar.due",
  // [T-066 · #115] มีผู้ตอบในคุยข้างเคียง (in-app ถึงผู้เปิดเธรด + ผู้ถือการ์ด · src/side/notify.ts) — ยังไม่มีใน NOTIFICATION_DEFAULTS seed เหมือน automation.notify
  "side_conversation.replied",
  // [M4-05] โควตา/ลิมิตอีเมล (D-041 M4-2) — bounded hunk ผ่าน PR Lead: ยังไม่มีใน NOTIFICATION_DEFAULTS seed (แจ้ง in-app เท่านั้น แบบเดียวกับ dsar.due)
  "mail.quota_warning",
  "mail.send_limit_exceeded",
  "mail.send_suspended",
  // [TM-04] โมดูลงานภายใน — emit ตรงจาก src/tasks (bridge src/notifications/tasks.ts) ไม่ผ่าน ticket_events
  // fresh seed มีค่า default ให้แล้ว (seed.ts) — ฐานที่ seed ไปแล้ว owner เติม app_settings.notification_defaults เอง (in-app ทำงานเสมอโดยไม่ต้องมี default)
  "task.assigned_to_me",
  "task.mentioned",
  "task.comment_added",
  "task.status_changed",
  "task.due_soon",
  "task.overdue",
]);
export type NotificationEvent = z.infer<typeof NotificationEvent>;

export const NotificationChannel = z.enum(["inapp", "push", "email", "telegram", "discord", "webhook"]);
export type NotificationChannel = z.infer<typeof NotificationChannel>;

export const NotificationDigest = z.enum(["none", "hourly", "daily"]);
export type NotificationDigest = z.infer<typeof NotificationDigest>;

/**
 * [SA-09 · #281] ความสามารถ**ส่งได้จริง**ของ channel ที่ runtime ประกาศ (ไม่ใช่ค่าที่ user ตั้ง)
 * push ปิดทั้ง release นี้ตาม owner decision — UI ต้อง disable ตามค่านี้ ไม่ใช่ซ่อนเองฝั่งเดียว
 */
export const NotificationChannelCapability = z.object({
  enabled: z.boolean(),
  reason: z.string().nullable(),
});
export type NotificationChannelCapability = z.infer<typeof NotificationChannelCapability>;

export const Notification = z.object({
  id: z.uuid(),
  event: NotificationEvent,
  ticket_id: z.string().nullable(), // ticket public id
  message_id: z.uuid().nullable(),
  title: z.string(),
  body: z.string().nullable(),
  deep_link: z.string().nullable(),
  read_at: z.iso.datetime().nullable(),
  created_at: z.iso.datetime(),
});
export type Notification = z.infer<typeof Notification>;

export const NotificationList = z.object({
  items: z.array(Notification),
  next_cursor: z.string().nullable(),
  unread_count: z.number().int(),
});
export type NotificationList = z.infer<typeof NotificationList>;

export const NotificationListQuery = z.object({
  cursor: z.string().optional(),
  unread: z.coerce.boolean().optional(),
  limit: z.coerce.number().int().min(1).max(50).default(50),
});
export type NotificationListQuery = z.infer<typeof NotificationListQuery>;

export const NotificationMarkRead = z.object({
  ids: z.array(z.uuid()).max(200).optional(),
  all: z.boolean().optional(),
});
export type NotificationMarkRead = z.infer<typeof NotificationMarkRead>;

/** `{prefs: {event: {channel: enabled}}, digest, quiet_outside_business_hours, business_hours_id}` (W3-CONTRACTS §5) */
// หมายเหตุ: ใช้ z.string() เป็น key ของ channel map (ไม่ใช่ z.record(NotificationChannel, …)) เพราะ zod 4 ทำให้ enum-key record
// กลายเป็น "exhaustive" (ต้องมีครบทุก key) ซึ่งขัดกับการ override บางส่วนต่อ channel ที่ R6/R2 ต้องการ — ตรวจ channel ด้วย NotificationChannel.safeParse ที่ผู้เรียกถ้าจำเป็น
export const NotificationPrefs = z.object({
  prefs: z.record(z.string(), z.record(z.string(), z.boolean())),
  digest: NotificationDigest,
  quiet_outside_business_hours: z.boolean(),
  business_hours_id: z.uuid().nullable(),
  /** [SA-09 · #281] runtime capability ต่อ channel — push: {enabled:false, reason:"push_disabled"} ใน release นี้ */
  capabilities: z.object({ push: NotificationChannelCapability }),
});
export type NotificationPrefs = z.infer<typeof NotificationPrefs>;

/** PUT body — ทุก field optional (merge เข้ากับค่าที่มี) */
export const NotificationPrefsUpdate = z.object({
  prefs: z.record(z.string(), z.record(z.string(), z.boolean())).optional(),
  digest: NotificationDigest.optional(),
  quiet_outside_business_hours: z.boolean().optional(),
  business_hours_id: z.uuid().nullable().optional(),
});
export type NotificationPrefsUpdate = z.infer<typeof NotificationPrefsUpdate>;

export const NotificationTarget = z.object({
  id: z.uuid(),
  channel: NotificationChannel,
  config: z.record(z.string(), z.unknown()),
  verified: z.boolean(),
  created_at: z.iso.datetime(),
});
export type NotificationTarget = z.infer<typeof NotificationTarget>;

export const NotificationTargetInput = z.object({
  channel: NotificationChannel,
  config: z.record(z.string(), z.unknown()).default({}),
});
export type NotificationTargetInput = z.infer<typeof NotificationTargetInput>;
